import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * The chat composer, as a shadcn primitive.
 *
 * A composer is not a textarea. It is a container that OWNS the focus ring, and
 * inside it sit a growing text field and a toolbar — so a click anywhere on the
 * box focuses the field, and the ring is drawn once around the whole thing
 * rather than around the input while the buttons sit outside it looking
 * detached.
 *
 * Composition, mirroring the rest of the library:
 *
 *   PromptInput            the bordered box; owns focus-within styling
 *   ├── PromptInputTextarea  auto-growing field; Enter sends, Shift+Enter breaks
 *   └── PromptInputToolbar   the row beneath
 *       ├── PromptInputTools  leading edge (attach, model, mode…)
 *       └── PromptInputSubmit trailing edge
 *
 * All spacing is logical, so the whole composer mirrors in RTL without a single
 * direction-specific rule.
 *
 * Everything here is presentational. What the buttons do belongs to the caller.
 */

function PromptInput({ className, ...props }: React.ComponentProps<"form">) {
  return (
    <form
      data-slot="prompt-input"
      className={cn(
        "group/prompt relative flex w-full flex-col gap-2 rounded-2xl border bg-card p-2",
        "shadow-[var(--elev-1)] transition-[color,box-shadow,border-color]",
        // The ring belongs to the BOX, not the field — the toolbar is inside it
        // and a ring around the textarea alone would cut the buttons out of the
        // control they belong to.
        /*
          A hairline, not a halo. The composer is auto-focused the moment agent
          mode opens, so a 3px accent ring is the first and loudest thing on an
          otherwise empty screen — it reads as an error state rather than as
          "you can type here". The border darkening is enough on a control this
          large; the ring is what a small input needs, not this.
        */
        /*
          The border darkens; nothing glows.

          The composer is auto-focused the moment agent mode opens, so an accent
          ring — even a thin one — is the loudest thing on an otherwise empty
          screen and reads as an error state rather than as "type here". A large
          control does not need the ring a small input does; it needs to look
          slightly more present than the surface behind it.
        */
        "focus-within:border-foreground/25",
        "has-aria-invalid:border-destructive has-aria-invalid:ring-destructive/20",
        className
      )}
      {...props}
    />
  )
}

/**
 * The field.
 *
 * Grows with its content to `maxRows`, then scrolls — so a pasted essay cannot
 * push the send button off the panel. Height is set imperatively because a
 * textarea has no intrinsic content height in CSS: you must reset to `auto` and
 * read `scrollHeight`, or it can only ever grow and never shrink again.
 */
function PromptInputTextarea({
  className,
  onKeyDown,
  onChange,
  /* Six lines is a paragraph. Past that the field is competing with the
     conversation it is meant to serve, and it scrolls instead. */
  maxRows = 6,
  onSubmitRequest,
  ref: forwardedRef,
  ...props
}: Omit<React.ComponentProps<"textarea">, "rows"> & {
  maxRows?: number
  /** Enter (without Shift) — the convention every chat surface shares. */
  onSubmitRequest?: () => void
}) {
  /**
   * Two owners, one node.
   *
   * This component needs the element to measure and resize it; the caller needs
   * it to focus the field when the panel opens. Taking `ref` and spreading it
   * with the rest of the props would have one silently win over the other — so
   * both are attached explicitly, and the caller's is honoured whether it is a
   * callback ref or an object ref.
   */
  const ref = React.useRef<HTMLTextAreaElement>(null)

  const attach = React.useCallback(
    (node: HTMLTextAreaElement | null) => {
      ref.current = node
      if (typeof forwardedRef === "function") forwardedRef(node)
      else if (forwardedRef) forwardedRef.current = node
    },
    [forwardedRef]
  )

  const resize = React.useCallback(
    (el: HTMLTextAreaElement) => {
      const line = parseFloat(getComputedStyle(el).lineHeight || "20")
      /*
        Measured from ZERO, not from whatever it currently is.

        `height: auto` on a textarea inside a flex column does not shrink it
        back — the browser keeps the box it already had, so scrollHeight
        returns the old height and the field could only ever grow. An empty
        composer ended up several lines tall and stayed there.
      */
      el.style.height = "0px"
      const wanted = el.scrollHeight
      el.style.height = `${Math.max(line, Math.min(wanted, line * maxRows))}px`
    },
    [maxRows]
  )

  // Re-fit when the value is changed from outside (cleared after sending).
  React.useEffect(() => {
    if (ref.current) resize(ref.current)
  }, [props.value, resize])

  return (
    <textarea
      ref={attach}
      data-slot="prompt-input-textarea"
      rows={1}
      // Overridable, but `auto` is the right default for a field that will hold
      // whichever language the person happens to be writing in.
      dir="auto"
      className={cn(
        "min-h-9 w-full resize-none bg-transparent px-2 pt-1.5 text-sm leading-relaxed",
        "outline-none placeholder:text-muted-foreground",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      onChange={(e) => {
        resize(e.currentTarget)
        onChange?.(e)
      }}
      onKeyDown={(e) => {
        onKeyDown?.(e)
        if (e.defaultPrevented) return
        // IME composition: Enter is confirming a candidate, not sending.
        if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
          e.preventDefault()
          onSubmitRequest?.()
        }
      }}
      {...props}
    />
  )
}

function PromptInputToolbar({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="prompt-input-toolbar"
      className={cn("flex items-center justify-between gap-2", className)}
      {...props}
    />
  )
}

function PromptInputTools({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="prompt-input-tools"
      className={cn(
        "flex items-center gap-0.5",
        // Icon buttons in here are quiet until hovered; the send button is the
        // only thing in the composer allowed to carry colour.
        "[&_button]:text-muted-foreground [&_button:hover]:text-foreground",
        className
      )}
      {...props}
    />
  )
}

/**
 * Send, or stop.
 *
 * One button with two jobs, because they occupy the same moment: while a reply
 * is streaming the only thing you want from that corner is to interrupt it, and
 * a separate stop control would sit dead for the other 99% of the time.
 */
function PromptInputSubmit({
  className,
  status = "idle",
  ...props
}: React.ComponentProps<"button"> & {
  status?: "idle" | "submitted" | "streaming"
}) {
  const busy = status === "submitted" || status === "streaming"
  return (
    <button
      type={busy ? "button" : "submit"}
      data-slot="prompt-input-submit"
      data-status={status}
      className={cn(
        "inline-flex size-8 shrink-0 items-center justify-center rounded-lg transition-all",
        "bg-[var(--action)] text-[var(--action-foreground)]",
        "hover:brightness-95 active:scale-95",
        "disabled:pointer-events-none disabled:opacity-40",
        "focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none",
        className
      )}
      {...props}
    />
  )
}

export {
  PromptInput,
  PromptInputTextarea,
  PromptInputToolbar,
  PromptInputTools,
  PromptInputSubmit,
}
